import { useCallback, useMemo } from 'react'
import { StatusMutator } from '@phrasee/phrasee-typings/Graphql/interfaces'
import isEqual from 'lodash/isEqual'

import Badge from 'common/components/Badge'
import Table, { CellValue, Column } from 'common/components/table'
import Base from 'common/components/table/cells/Base'
import Filter from 'common/components/table/filters/Filter'
import Tooltip from 'common/components/Tooltip'
import {
  CheckCircle as CheckCircleIcon,
  Clock as ClockIcon,
  Stop as StopIcon,
  Thumb as ThumbIcon,
} from 'common/icons'

import VariantSentimentHighlighter from './VariantSentimentHighlighter'

const VARIANTS_WIDTH = 400
const DEFAULT_FILTERS = ['live']

export type Data = {
  [key: string]:
    | number
    | string
    | { word: string; score: number }
    | {}
    | undefined
} & {
  color: string
} & {
  variant: string
} & {
  weightsPerSentiment: { [key: string]: { word: string; score: number }[] } | {}
} & {
  status?: StatusMutator
}

export type Row = Data & { key: string }

type Props = {
  data: Row[]
  selectedRowKeys: string[]
  onChangeSelectRow: (selectedRowKeys: string[]) => void
  isReactCampaign: boolean
  selectedSentiment: string
}

const defaultFilters = [{ id: 'status', value: DEFAULT_FILTERS }]

const SentimentAnalysisTable = ({
  data,
  selectedRowKeys,
  onChangeSelectRow,
  isReactCampaign,
  selectedSentiment,
}: Props) => {
  const columns: Column<Row>[] = useMemo(
    () => [
      {
        Header: 'Variant',
        accessor: 'variant',
        width: VARIANTS_WIDTH,
        Cell: (cellValue: CellValue) => {
          const { variant, weightsPerSentiment, isControlVariant } =
            cellValue.row.original
          return (
            <Base>
              <div className="flex items-center" data-testid="variant-cell">
                <div>
                  <VariantSentimentHighlighter
                    variant={variant}
                    weights={
                      selectedSentiment
                        ? (weightsPerSentiment[selectedSentiment] ?? [])
                        : []
                    }
                  />
                </div>
                {isControlVariant && (
                  <Badge
                    className="ml-6 uppercase"
                    size="small"
                    text="Control"
                    variant="neutral"
                  />
                )}
              </div>
            </Base>
          )
        },
      },
      {
        Header: 'Sentiment scores',
        columns: [
          isReactCampaign
            ? {
                Header: 'Status',
                accessor: 'status',
                disableSortBy: true,
                aggregate: 'count',
                Aggregated: ({ value }) => `${value} Names`,
                width: 90,
                Cell: (cellValue: CellValue) => {
                  const { status } = cellValue.row.original
                  return (
                    <Base className="justify-center">
                      <Tooltip overlay={status}>
                        <span>{mapIconsToStatus(status)}</span>
                      </Tooltip>
                    </Base>
                  )
                },
                Filter: Filter({
                  defaultFilters: defaultFilters[0].value,
                }),
                filter: 'includesSome',
              }
            : undefined,
          {
            Header: 'Impressed',
            accessor: 'impressed',
            aggregate: 'uniqueCount',
            Aggregated: ({ value }) => `${value} Unique Names`,
          },
          {
            Header: 'Helpful',
            accessor: 'helpful',
            aggregate: 'uniqueCount',
            Aggregated: ({ value }) => `${value} Unique Names`,
          },
          {
            Header: 'Curious',
            accessor: 'curious',
            aggregate: 'uniqueCount',
            Aggregated: ({ value }) => `${value} Unique Names`,
          },
          {
            Header: 'Excited',
            accessor: 'excited',
            aggregate: 'uniqueCount',
            Aggregated: ({ value }) => `${value} Unique Names`,
          },
          {
            Header: 'Surprising',
            accessor: 'surprising',
            aggregate: 'uniqueCount',
            Aggregated: ({ value }) => `${value} Unique Names`,
          },
          {
            Header: 'Appreciative',
            accessor: 'appreciative',
          },
          {
            Header: 'Urgent',
            accessor: 'urgent',
          },
        ].filter(Boolean),
      },
    ],
    [isReactCampaign, selectedSentiment]
  )
  const handleSelectRow = useCallback(
    (rows: Row[]) => {
      if (
        !isEqual(
          rows.map((row) => row.key),
          selectedRowKeys
        )
      ) {
        onChangeSelectRow(rows.map((row) => row.variant))
      }
    },
    [onChangeSelectRow, selectedRowKeys]
  )
  const initialState = useMemo(
    () => ({
      selectedRowIds: selectedRowKeys.reduce(
        (previousValue, key) => ({
          ...previousValue,
          [data.findIndex((value) => value.key === key)]: true,
        }),
        {}
      ),
      filters: defaultFilters,
    }),
    [data, selectedRowKeys]
  )
  const getCheckboxProps = useCallback((row) => {
    return {
      backgroundColor: row.original.color,
    }
  }, [])

  return (
    <div className="bg-white shadow-md m-6 updated-component">
      <Table<Row>
        data-testid="engage-sentiments-table"
        columns={columns}
        data={data}
        initialState={initialState}
        autoResetFilters={false}
        rowSelection="multiple"
        onSelectRow={handleSelectRow}
        getCheckboxProps={getCheckboxProps}
      />
    </div>
  )
}

export default SentimentAnalysisTable

const mapIconsToStatus = (status: StatusMutator) => {
  switch (status) {
    case 'pending':
      return (
        <ClockIcon
          isDefaultColor={false}
          className="text-coolGray-400"
          size={4}
        />
      )
    case 'approved':
      return (
        <ThumbIcon isDefaultColor={false} className="text-lime-500" size={4} />
      )
    case 'live':
      return (
        <CheckCircleIcon
          isDefaultColor={false}
          className="text-maroon-400"
          size={4}
        />
      )
    case 'dropped':
      return (
        <StopIcon
          isDefaultColor={false}
          className="text-coolGray-400"
          size={4}
        />
      )
    default:
      return <>status</>
  }
}
